/**
 * THE I18N CONTEXT — the value the provider publishes, and the hook that reads
 * it. No provider, no catalog loading, no components.
 *
 * Hoisted out of `I18nProvider.tsx` so `TSlots.tsx` can read `useT()` without
 * importing that module. `I18nProvider.tsx` re-exports `TSlots` (it moved to
 * its own file when the provider grew past the ~300-line ceiling) while
 * `TSlots.tsx` imported `useT` back — a ring, and the same shape the file-size
 * splits in `annotations/`, `host-actions/` and `interactive/` each produced.
 *
 * A leaf: it imports nothing from the provider, so both edges point one way
 * (`architecture.md` — hoist the shared vocabulary, keep every edge
 * one-directional). `I18nProvider.tsx` re-exports `useT` and
 * `I18nContextValue`, so nothing that already imported them moves.
 */

import { createContext, useContext } from 'react';

import type { ArabicNumerals, Locale } from '@syncanix/shared';

export interface I18nContextValue {
  /** The active locale code. */
  readonly locale: Locale;
  /** Change the active locale (re-runs the `<html dir>` effect). */
  readonly setLocale: (locale: Locale) => void;
  /**
   * The Arabic numeral preference. Per the locked spec
   * (`product/trust/localization-rtl.md` § Numerals, ADR-0141) every
   * locale defaults to Western (0-9); `'eastern'` opts Arabic into
   * Arabic-Indic (٠-٩). Only meaningful for Arabic — a no-op for
   * every other locale.
   */
  readonly numerals: ArabicNumerals;
  /** Change the Arabic numeral preference (persisted by the app). */
  readonly setNumerals: (numerals: ArabicNumerals) => void;
  /**
   * The resolved Unicode numbering system for the active locale +
   * numeral preference (`getLocaleNumberingSystem(locale, numerals)`).
   * Feed into `Intl.NumberFormat` / `toLocaleString`'s `numberingSystem`
   * so a user's Eastern opt-in flows through every number on the surface.
   */
  readonly numberingSystem: string;
  /**
   * Resolve a message key to its translated string. Unknown keys
   * fall back to the key itself; `{param}` placeholders are
   * replaced with the matching value from `params`.
   */
  readonly t: (key: string, params?: Record<string, string | number>) => string;
}

export const I18nContext = createContext<I18nContextValue | null>(null);

/**
 * Provider absence is LOUD (locked iter 387): this throws when called outside
 * the provider. The web app's root composition MUST include `<I18nProvider>`;
 * a missing wrapper is a structural bug, not a recoverable runtime condition.
 */
export function useT(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (ctx === null) {
    throw new Error(
      'useT() must be used inside <I18nProvider>. ' +
        'Wrap your app root with <I18nProvider> — see apps/web/src/core/providers/Providers.tsx.',
    );
  }
  return ctx;
}
